import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Plus, Trash2, X, Loader2, Mail, Building2, ArrowRight, ShieldOff, Pencil, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

const API = '/api';

/**
 * BDRoutingRulesCard
 * --------------------------------------------------------------------------
 * Inline section on the pharma profile page. Lets the user paste a BD email
 * instruction (e.g. "Immunology opportunities → Kalpana, skip me — Dov"),
 * runs it through the LLM parser, then shows a confirm modal to review +
 * edit + save the extracted rule. Saved rules render as inline chips below.
 *
 * Hard-suppress semantics: rule.bypass[] is excluded from AI Mapping for
 * that TA; rule.route_to is the SOLE recipient.
 */
export default function BDRoutingRulesCard({ profile, onChanged }) {
  const rules = (profile?.bd_routing_rules || []).filter(Boolean);
  const [pasteEmailOpen, setPasteEmailOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [draft, setDraft] = useState(null);     // { therapeutic_areas, route_to, bypass, note, source_*, confidence, rationale }
  const [saving, setSaving] = useState(false);

  const handleParse = async () => {
    if (!pasteText.trim()) {
      toast.error('Paste the email text first');
      return;
    }
    setParsing(true);
    try {
      const res = await axios.post(
        `${API}/company-profiles/${profile.id}/bd-routing-rules/parse`,
        { email_text: pasteText },
        { withCredentials: true },
      );
      const s = res.data.suggestion;
      // route_to from the parser is always a list; tolerate legacy single-dict.
      const rtList = Array.isArray(s.route_to)
        ? s.route_to
        : (s.route_to && (s.route_to.name || s.route_to.email) ? [s.route_to] : []);
      setDraft({
        therapeutic_areas: s.therapeutic_areas || [],
        route_to: rtList.length > 0
          ? rtList.map(r => ({ name: r.name || '', email: r.email || '', designation: r.designation || '' }))
          : [{ name: '', email: '', designation: '' }],
        bypass: s.bypass || [],
        note: s.rationale || '',
        source_excerpt: pasteText,
        source_sender_name: s.source_sender_name || '',
        source_sender_email: s.source_sender_email || '',
        source_sender_date: '',
        confidence: s.confidence || 'low',
        rationale: s.rationale || '',
      });
      setPasteEmailOpen(false);
      setReviewOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Parser failed — try again or use manual entry');
    } finally {
      setParsing(false);
    }
  };

  const handleSave = async () => {
    if (!draft?.therapeutic_areas?.length) {
      toast.error('At least one therapeutic area is required');
      return;
    }
    const validRoutes = (draft.route_to || []).filter(r => (r.name || '').trim() || (r.email || '').trim());
    if (validRoutes.length === 0) {
      toast.error('At least one Route-to person (name or email) is required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/company-profiles/${profile.id}/bd-routing-rules`,
        { ...draft, route_to: validRoutes },
        { withCredentials: true },
      );
      toast.success('BD routing rule saved');
      setReviewOpen(false);
      setDraft(null);
      setPasteText('');
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ruleId, ruleLabel) => {
    if (!window.confirm(`Delete the "${ruleLabel}" routing rule? AI Mapping will go back to surfacing all contacts for this TA.`)) return;
    try {
      await axios.delete(
        `${API}/company-profiles/${profile.id}/bd-routing-rules/${ruleId}`,
        { withCredentials: true },
      );
      toast.success('Rule deleted');
      onChanged?.();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleSetMode = async (rule, mode) => {
    try {
      await axios.patch(`${API}/company-profiles/${profile.id}/bd-routing-rules/${rule.id}/mode`, { mode }, { withCredentials: true });
      toast.success(mode === 'additive'
        ? 'Rule is now additive — the routed address AND contacts with a matching preference will both receive matching assets'
        : 'Rule is now exclusive — only the routed address receives matching assets');
      onChanged?.();
    } catch {
      toast.error('Failed to update rule mode');
    }
  };

  const handleAddManual = () => {
    setDraft({
      therapeutic_areas: [],
      route_to: [{ name: '', email: '', designation: '' }],
      bypass: [],
      note: '',
      source_excerpt: '',
      source_sender_name: '',
      source_sender_email: '',
      source_sender_date: '',
      confidence: 'high',
      rationale: 'Manually entered',
    });
    setReviewOpen(true);
  };

  return (
    <>
      <div
        className={`rounded-t-lg border border-b-0 px-4 py-3 ${rules.length > 0 ? 'bg-violet-50/50 border-violet-200' : 'bg-slate-50 border-slate-200'}`}
        data-testid="bd-routing-rules-card"
      >
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className={`w-4 h-4 shrink-0 ${rules.length > 0 ? 'text-violet-600' : 'text-slate-400'}`} />
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider shrink-0">
            Explicit rules · from BD emails
          </span>
          <Badge variant="secondary" className="text-[10px] h-5">{rules.length}</Badge>
          <span className="text-[11px] text-slate-400 ml-1">
            {`"{TA} opportunities → {specific person}, skip others"`}
          </span>
          <div className="ml-auto flex items-center gap-1.5">
            <Button
              size="sm"
              onClick={() => { setPasteEmailOpen(true); setPasteText(''); }}
              className="h-7 text-xs bg-violet-600 hover:bg-violet-700"
              data-testid="bd-rule-paste-email-btn"
            >
              <Sparkles className="w-3 h-3 mr-1.5" /> Paste Email
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleAddManual}
              className="h-7 text-xs"
              data-testid="bd-rule-manual-btn"
            >
              <Plus className="w-3 h-3 mr-1" /> Manual
            </Button>
          </div>
        </div>

        {rules.length > 0 ? (
          <div className="space-y-1.5 mt-3">
            {rules.map(r => {
              const taLabel = (r.therapeutic_areas || []).includes('*')
                ? 'All other TAs (catch-all)'
                : (r.therapeutic_areas || []).join(', ');
              const isInactive = r.active === false;
              return (
                <div
                  key={r.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded border bg-white ${isInactive ? 'opacity-50' : ''}`}
                  data-testid={`bd-rule-row-${r.id}`}
                >
                  <Badge className="bg-sky-100 text-sky-700 border-sky-200 text-[10px] shrink-0">
                    {taLabel || '—'}
                  </Badge>
                  <ArrowRight className="w-3 h-3 text-slate-300 shrink-0" />
                  <button
                    type="button"
                    onClick={() => handleSetMode(r, (r.mode || 'exclusive') === 'additive' ? 'exclusive' : 'additive')}
                    className={`text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border shrink-0 cursor-pointer ${(r.mode || 'exclusive') === 'additive' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' : 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100'}`}
                    title={(r.mode || 'exclusive') === 'additive'
                      ? 'ADDITIVE: the routed address is added, and contacts whose stated preference matches the asset also receive it. Click to make exclusive.'
                      : 'EXCLUSIVE: ONLY the routed address may receive matching assets; everyone else is hidden. Click to make additive (also send to contacts with a matching preference).'}
                    data-testid={`bd-rule-mode-${r.id}`}
                  >
                    {(r.mode || 'exclusive') === 'additive' ? '+ also specialists' : 'only'}
                  </button>
                  <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                    <Mail className="w-3 h-3 text-emerald-600 shrink-0" />
                    {(() => {
                      const rt = r.route_to;
                      const list = Array.isArray(rt) ? rt : (rt && (rt.name || rt.email) ? [rt] : []);
                      if (list.length === 0) {
                        return <span className="text-xs text-slate-400 italic">—</span>;
                      }
                      return list.map((p, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span className="text-[10px] text-slate-300">·</span>}
                          <span className="text-xs font-medium text-slate-800 truncate">{p.name || p.email}</span>
                          {p.email && p.name && (
                            <span className="text-[10px] text-slate-400 truncate">&lt;{p.email}&gt;</span>
                          )}
                        </React.Fragment>
                      ));
                    })()}
                  </div>
                  {r.bypass?.length > 0 && (
                    <div className="flex items-center gap-1 ml-2">
                      <ShieldOff className="w-3 h-3 text-rose-400 shrink-0" />
                      <span className="text-[10px] text-rose-500 truncate">
                        skip {r.bypass.map(b => b.name || b.email).join(', ')}
                      </span>
                    </div>
                  )}
                  {r.source_sender_name && (
                    <span className="text-[9px] text-slate-400 ml-auto shrink-0" title={r.source_excerpt}>
                      via {r.source_sender_name}
                    </span>
                  )}
                  <button
                    onClick={() => handleDelete(r.id, taLabel)}
                    className="text-slate-300 hover:text-rose-500 transition shrink-0 ml-1"
                    title="Delete rule"
                    data-testid={`bd-rule-delete-${r.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-[11px] text-slate-500 ml-6">
            No per-TA routing yet. When a BD lead tells you &quot;send X-type deals to Y&quot;, paste their email here — AI will extract the rule.
          </div>
        )}
      </div>

      {/* Paste Email Dialog — captures raw text and runs LLM parser */}
      <Dialog open={pasteEmailOpen} onOpenChange={setPasteEmailOpen}>
        <DialogContent className="max-w-2xl" data-testid="bd-rule-paste-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" />
              Paste BD Email
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Paste the BD lead&apos;s instruction email verbatim. Claude will extract: TA + the person it should route to + anyone explicitly told to skip.
            </p>
          </DialogHeader>
          <div className="py-2">
            <Textarea
              value={pasteText}
              onChange={e => setPasteText(e.target.value)}
              placeholder={`e.g.\n\nHi Amit,\n\nImmunology opportunities should be sent to Kalpana. You don't need to copy me. thx!\n\nDov`}
              className="min-h-[200px] text-sm font-mono"
              data-testid="bd-rule-paste-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasteEmailOpen(false)} disabled={parsing}>Cancel</Button>
            <Button
              onClick={handleParse}
              disabled={parsing || !pasteText.trim()}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="bd-rule-parse-btn"
            >
              {parsing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {parsing ? 'Parsing…' : 'Parse with Claude'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Dialog — confirm + edit before saving */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bd-rule-review-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Review Routing Rule</DialogTitle>
            {draft?.confidence && (
              <div className="flex items-center gap-2 mt-1">
                <Badge className={`text-[10px] ${
                  draft.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' :
                  draft.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                  'bg-rose-100 text-rose-700'
                }`}>
                  {draft.confidence} confidence
                </Badge>
                {draft.rationale && <span className="text-[11px] text-slate-500 italic">{draft.rationale}</span>}
              </div>
            )}
          </DialogHeader>

          {draft && (
            <div className="space-y-3 py-2">
              {/* Therapeutic Areas — controlled autocomplete against the
                  canonical TA list (kept in sync with IntelligenceHubPage). */}
              <div>
                <label className="text-xs font-semibold text-slate-600">Therapeutic Areas</label>
                {!draft.therapeutic_areas.includes('*') && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {draft.therapeutic_areas.map(ta => (
                    <Badge key={ta} className="bg-sky-50 text-sky-700 border border-sky-200 text-[11px] gap-1">
                      {ta}
                      <button
                        onClick={() => setDraft({ ...draft, therapeutic_areas: draft.therapeutic_areas.filter(t => t !== ta) })}
                        className="hover:text-rose-500"
                      >×</button>
                    </Badge>
                  ))}
                  <TAAutocomplete
                    excluded={draft.therapeutic_areas}
                    onPick={(ta) => setDraft({ ...draft, therapeutic_areas: [...draft.therapeutic_areas, ta] })}
                  />
                </div>
                )}
                {/* Aug 2026 · catch-all rule — covers every asset whose TA
                    matches NO specific rule (e.g. "everything else → GenMed BD") */}
                <label className="flex items-start gap-2 mt-2 text-[11px] text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={draft.therapeutic_areas.includes('*')}
                    onChange={(e) => setDraft({ ...draft, therapeutic_areas: e.target.checked ? ['*'] : [] })}
                    data-testid="catch-all-rule-checkbox"
                  />
                  <span><strong>Catch-all</strong> — applies to ALL other therapeutic areas (anything not covered by a specific rule above). Use this to route everything else to the general partnering email (e.g. GenMed BD).</span>
                </label>
              </div>

              {/* Route To — supports MULTIPLE recipients (e.g. "connect with Leslie and Mike") */}
              <div className="border border-emerald-200 rounded-md p-3 bg-emerald-50/40">
                <div className="flex items-center gap-1.5 mb-2">
                  <Mail className="w-3.5 h-3.5 text-emerald-600" />
                  <label className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Route To</label>
                  <Badge variant="secondary" className="text-[10px] h-4 bg-emerald-100 text-emerald-700">
                    {(draft.route_to || []).length}
                  </Badge>
                  <span className="text-[10px] text-emerald-600 ml-1">All listed will receive outreach for this TA.</span>
                </div>
                <div className="space-y-2">
                  {(draft.route_to || []).map((r, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`bd-rule-route-row-${i}`}>
                      <Input
                        value={r.name}
                        onChange={e => {
                          const next = [...draft.route_to];
                          next[i] = { ...next[i], name: e.target.value };
                          setDraft({ ...draft, route_to: next });
                        }}
                        placeholder="Name (e.g. Leslie Cousens)"
                        className="h-8 text-xs col-span-5"
                        data-testid={`bd-rule-route-name-input-${i}`}
                      />
                      <Input
                        value={r.email}
                        onChange={e => {
                          const next = [...draft.route_to];
                          next[i] = { ...next[i], email: e.target.value };
                          setDraft({ ...draft, route_to: next });
                        }}
                        placeholder="Email"
                        type="email"
                        className="h-8 text-xs col-span-5"
                        data-testid={`bd-rule-route-email-input-${i}`}
                      />
                      <Input
                        value={r.designation}
                        onChange={e => {
                          const next = [...draft.route_to];
                          next[i] = { ...next[i], designation: e.target.value };
                          setDraft({ ...draft, route_to: next });
                        }}
                        placeholder="Designation (optional)"
                        className="h-8 text-xs col-span-11"
                        data-testid={`bd-rule-route-designation-input-${i}`}
                      />
                      {(draft.route_to || []).length > 1 && (
                        <button
                          onClick={() => setDraft({ ...draft, route_to: draft.route_to.filter((_, j) => j !== i) })}
                          className="col-span-1 row-span-2 self-start text-slate-300 hover:text-rose-500 mt-1.5"
                          title="Remove this recipient"
                          data-testid={`bd-rule-route-remove-${i}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setDraft({ ...draft, route_to: [...(draft.route_to || []), { name: '', email: '', designation: '' }] })}
                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 hover:text-emerald-900"
                  data-testid="bd-rule-add-route-btn"
                >
                  <Plus className="w-3 h-3" /> Add another recipient
                </button>
              </div>

              {/* Bypass */}
              <div className="border border-rose-200 rounded-md p-3 bg-rose-50/40">
                <div className="flex items-center gap-1.5 mb-2">
                  <ShieldOff className="w-3.5 h-3.5 text-rose-500" />
                  <label className="text-xs font-semibold text-rose-700 uppercase tracking-wide">Skip / Bypass</label>
                  <span className="text-[10px] text-rose-500 ml-1">Will be excluded from AI Mapping for this TA</span>
                </div>
                {draft.bypass.length > 0 && (
                  <div className="space-y-1 mb-2">
                    {draft.bypass.map((b, i) => (
                      <div key={i} className="flex items-center gap-2 bg-white rounded px-2 py-1 text-xs">
                        <span className="font-medium">{b.name || '—'}</span>
                        {b.email && <span className="text-slate-500">&lt;{b.email}&gt;</span>}
                        <button
                          onClick={() => setDraft({ ...draft, bypass: draft.bypass.filter((_, j) => j !== i) })}
                          className="ml-auto text-rose-300 hover:text-rose-600"
                        ><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Name (optional)"
                    id={`bd-rule-bypass-name-${profile.id}`}
                    className="h-8 text-xs"
                    data-testid="bd-rule-bypass-name-input"
                  />
                  <Input
                    placeholder="Email"
                    type="email"
                    id={`bd-rule-bypass-email-${profile.id}`}
                    className="h-8 text-xs"
                    data-testid="bd-rule-bypass-email-input"
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        const nameEl = document.getElementById(`bd-rule-bypass-name-${profile.id}`);
                        const name = (nameEl?.value || '').trim();
                        const email = (e.target.value || '').trim();
                        if (name || email) {
                          setDraft({ ...draft, bypass: [...draft.bypass, { name, email }] });
                          if (nameEl) nameEl.value = '';
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Press Enter on the email field to add.</p>
              </div>

              {/* Source */}
              {draft.source_excerpt && (
                <details className="text-[11px] text-slate-500">
                  <summary className="cursor-pointer hover:text-slate-700">Source email (kept for audit)</summary>
                  <pre className="whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded p-2 mt-1 font-mono text-[10px] max-h-32 overflow-y-auto">
                    {draft.source_excerpt}
                  </pre>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Input
                      value={draft.source_sender_name}
                      onChange={e => setDraft({ ...draft, source_sender_name: e.target.value })}
                      placeholder="Sender name"
                      className="h-7 text-xs"
                    />
                    <Input
                      value={draft.source_sender_email}
                      onChange={e => setDraft({ ...draft, source_sender_email: e.target.value })}
                      placeholder="Sender email"
                      className="h-7 text-xs"
                    />
                  </div>
                </details>
              )}

              {/* Mode */}
              <div>
                <label className="text-xs font-semibold text-slate-600">Who receives matching assets?</label>
                <div className="flex gap-2 mt-1">
                  {[['exclusive', 'Only the routed address', 'Everyone else at this company is hidden for these TAs.'], ['additive', 'Routed address + specialists', 'Also send to contacts whose stated preference matches the asset (e.g. Immunology BD people).']].map(([val, label, hint]) => (
                    <button key={val} type="button" onClick={() => setDraft({ ...draft, mode: val })}
                      className={`flex-1 text-left rounded-md border px-3 py-2 text-xs transition-colors ${(draft.mode || 'exclusive') === val ? 'border-violet-500 bg-violet-50 text-violet-900' : 'border-slate-200 hover:bg-slate-50 text-slate-700'}`}
                      data-testid={`bd-rule-mode-option-${val}`}>
                      <div className="font-semibold">{label}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="text-xs font-semibold text-slate-600">Note (free-text)</label>
                <Textarea
                  value={draft.note}
                  onChange={e => setDraft({ ...draft, note: e.target.value })}
                  placeholder="Optional context for the rule"
                  className="text-xs mt-1 min-h-[50px]"
                  data-testid="bd-rule-note-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={saving || !draft?.therapeutic_areas?.length || !(draft?.route_to || []).some(r => (r.name || '').trim() || (r.email || '').trim())}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="bd-rule-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Saving…' : 'Save Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Canonical TA list (mirror of IntelligenceHubPage) ────────────────────
const CANONICAL_TAS = [
  'Oncology', 'Immunology', 'Vaccines', 'Rare Disease', 'Neurology',
  'CNS', 'Cardiovascular', 'Metabolic', 'Respiratory', 'Infectious Disease',
  'Dermatology', 'Ophthalmology', 'Hematology', 'Endocrinology', 'Nephrology',
  'Rheumatology', 'Psychiatry', 'Gastrointestinal', 'Pain',
];

function TAAutocomplete({ excluded, onPick }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const excludedLower = new Set((excluded || []).map(t => (t || '').toLowerCase()));
  const filtered = CANONICAL_TAS
    .filter(ta => !excludedLower.has(ta.toLowerCase()))
    .filter(ta => query ? ta.toLowerCase().includes(query.toLowerCase()) : true);
  const pick = (ta) => {
    onPick(ta);
    setQuery('');
    setOpen(false);
  };
  return (
    <div className="relative">
      <Input
        placeholder="+ Add TA"
        className="h-7 w-40 text-xs"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && filtered[0]) { e.preventDefault(); pick(filtered[0]); }
          if (e.key === 'Escape') setOpen(false);
        }}
        data-testid="bd-rule-add-ta-input"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 top-8 left-0 min-w-[180px] max-h-64 overflow-auto bg-white border border-slate-200 rounded shadow-lg text-xs">
          {filtered.map(ta => (
            <button
              key={ta}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(ta); }}
              className="w-full text-left px-3 py-1.5 hover:bg-sky-50 text-slate-700"
              data-testid={`ta-option-${ta.toLowerCase().replace(/\s+/g, '-')}`}
            >{ta}</button>
          ))}
        </div>
      )}
      {open && query && filtered.length === 0 && (
        <div className="absolute z-50 top-8 left-0 min-w-[180px] bg-white border border-slate-200 rounded shadow-lg text-xs px-3 py-2 text-slate-500">
          {`No match. Press Enter to add "${query}" as custom TA.`}
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); pick(query.trim()); }}
            className="block mt-1 text-sky-600 hover:underline"
          >Add custom</button>
        </div>
      )}
    </div>
  );
}

